import React, { useEffect } from 'react';
import styles from './SidePanel.module.scss';
import { APP_ROUTES, Routes } from "../../types/constants";
import Button from "../Buttons/Button/Button";
import { ButtonStyleType, TextSize } from "../../types/button.types";
import { useAppState } from "../../providers/app-state.provider";
import logo from '../../assets/images/combattv-logo.png';
import { useUserState } from "../../providers/user-state.provider.ts";
import router from "../../providers/router";
import { useAuthModal } from "../../providers/auth-modal.provider";
import { shareCombatTv } from "../../utils/share.utils";
import { toast } from "react-toastify";
import { useSubscriptionStatus } from "../../providers/subscription-status.provider";
import { isAdmin } from "../../utils/roles";

interface SidePanelProps { }

const SidePanel = (props: SidePanelProps) => {
    const { cycleSidePanel } = useAppState();
    const userData = useUserState();
    const userInfo = userData.getUserInfo();
    const { openModal } = useAuthModal();
    const { hasActiveSubscription } = useSubscriptionStatus();
    const isAdminUser = isAdmin(userInfo?.roles);
    const baseRoutes = isAdminUser
        ? [...APP_ROUTES, { name: 'Admin', route: Routes.ADMIN }]
        : APP_ROUTES;
    const routes = hasActiveSubscription
        ? baseRoutes.filter((route) => route.route !== Routes.PRICING)
        : baseRoutes;

    const handleShare = async () => {
        const result = await shareCombatTv({
            title: 'Combat TV',
            text: 'Share Combat TV with your friends.'
        });

        if (result === 'copied') {
            toast.success('Link copied to clipboard.');
        }

        if (result === 'failed') {
            toast.error('Unable to share. Please copy the link.');
        }

        cycleSidePanel();
    };

    const logout = () => {
        userData.clearUserInfo();
        cycleSidePanel();
        router.navigate(Routes.HOME);
    }

    const logIn = () => {
        openModal('sign_in');
        cycleSidePanel();
    }

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, []);

    return (
        <div className={styles['all']}>
            <div className={styles['panel']} role="dialog" aria-modal="true" aria-label="Mobile navigation">
                <div className={styles['panel-header']}>
                    <img src={logo} alt={'Combat TV'} className={styles['logo']} />
                    <button
                        type="button"
                        className={styles['close-button']}
                        onClick={() => cycleSidePanel()}
                        aria-label="Close menu"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div className={styles['panel-content']}>
                    <div className={styles['routes-container']}>
                        {routes.map(route => (
                            <a key={route.name} href={route.route} onClick={() => router.navigate(route.route)} className={styles['route']}>
                                {route.name?.toUpperCase()}
                            </a>
                        ))}
                        <div className={styles['explore-section']}>
                            <span className={styles['explore-title']}>CONNECT</span>
                            <button
                                type="button"
                                className={styles['route']}
                                onClick={handleShare}
                            >
                                SHARE
                            </button>
                        </div>
                    </div>

                    <div className={styles['user-routes-container']}>
                        <div className={'mt-2'}>
                            {
                                userData.isLoggedIn ?
                                    <a href={Routes.PROFILE} onClick={() => router.navigate(Routes.PROFILE)} className={styles['route']}>ACCOUNT</a> : <></>
                            }
                        </div>

                        <div className={'mt-2 ' + styles['signin']}>
                            {
                                userData.isLoggedIn ?
                                    <Button text={'SIGN OUT'} styleType={ButtonStyleType.FILL} textSize={TextSize.LARGE} callback={() => logout()} /> :
                                    <Button text={'SIGN IN'} styleType={ButtonStyleType.FILL} textSize={TextSize.LARGE} callback={() => logIn()} />
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles['mask']} onTouchStart={() => cycleSidePanel()} onClick={() => cycleSidePanel()}></div>
        </div>
    );
}

export default SidePanel;
